import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const TH = "text-left text-[10px] uppercase tracking-wider text-[#6B7280] px-2 py-1.5 border-b border-gray-200";
const TD = "px-2 py-1.5 text-xs text-[#0E2240] border-b border-gray-100";
const pct = (v) => (v == null ? "—" : `${(v * 100).toFixed(1)}%`);

function SlaBadge({ v }) {
  const c = v === "Breached" ? "#BB162B" : v === "At Risk" ? "#D97706" : "#1B7F3B";
  return <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-sm text-white" style={{ backgroundColor: c }}>{v}</span>;
}

export default function VpDashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    api.get("/analytics/vp").then(({ data }) => setD(data)).catch(() => {});
  }, []);

  const open = showAll ? d?.branch_open : d?.branch_open?.slice(0, 15);

  return (
    <div data-testid="vp-dashboard">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#6B7280]">Vertical Command · {d?.vertical || "…"}</div>
        <h1 className="font-serif text-3xl sm:text-4xl text-[#0E2240] tracking-tight mt-1">Welcome, {user?.full_name?.split(" ")[0]}</h1>
      </div>

      {d?.my_pending?.length > 0 && (
        <div className="mb-6" data-testid="vp-my-pending">
          <h2 className="font-serif text-base text-[#0E2240] mb-2">Waiting on you — {d.my_pending.length} approval{d.my_pending.length > 1 ? "s" : ""}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
            {d.my_pending.map((r) => (
              <Link key={r.req_id} to={`/requisitions/${r.req_id}`}
                className="bg-white border border-[#D97706]/50 rounded-sm p-3 hover:border-[#0E2240] transition-colors duration-200 block">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-[#1E5AA8]">{r.req_no}</span>
                  {r.sla_status && <SlaBadge v={r.sla_status} />}
                </div>
                <div className="text-sm text-[#0E2240] mt-1">{r.desig_name} · {r.branch_name}</div>
                <div className="text-[11px] text-[#6B7280] mt-0.5 tabular-nums">{r.days_waiting != null ? `${r.days_waiting}d in your queue` : ""}</div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-sm p-4 sm:p-5 mb-6" data-testid="vp-league">
        <h2 className="font-serif text-base text-[#0E2240] mb-3">Zone league table</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr>
              <th className={TH}>#</th><th className={TH}>Zone</th><th className={TH}>Sanctioned</th><th className={TH}>Active</th>
              <th className={TH}>Fill %</th><th className={TH}>Open Reqs</th><th className={TH}>Avg TTF</th><th className={TH}>Offer Drop %</th>
            </tr></thead>
            <tbody>
              {(d?.league || []).map((z) => (
                <tr key={z.zone_name} data-testid={`league-row-${z.rank}`}>
                  <td className={`${TD} tabular-nums font-semibold`}>{z.rank}</td>
                  <td className={TD}>{z.zone_name}</td>
                  <td className={`${TD} tabular-nums`}>{z.sanctioned}</td>
                  <td className={`${TD} tabular-nums`}>{z.active}</td>
                  <td className={`${TD} tabular-nums font-semibold ${z.fill != null && z.fill < 0.85 ? "text-[#BB162B]" : "text-[#1B7F3B]"}`}>{pct(z.fill)}</td>
                  <td className={`${TD} tabular-nums`}>{z.open_reqs}</td>
                  <td className={`${TD} tabular-nums`}>{z.avg_ttf != null ? `${z.avg_ttf}d` : "—"}</td>
                  <td className={`${TD} tabular-nums`}>{pct(z.offer_drop_rate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-8">
        <div className="bg-white border border-gray-200 rounded-sm p-4 sm:p-5" data-testid="vp-open-positions">
          <h2 className="font-serif text-base text-[#0E2240] mb-3">Open positions in my vertical</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr><th className={TH}>MRF</th><th className={TH}>Branch</th><th className={TH}>Role</th><th className={TH}>Stuck At</th><th className={TH}>Days</th></tr></thead>
              <tbody>
                {(open || []).map((r) => (
                  <tr key={r.req_id}>
                    <td className={TD}><Link to={`/requisitions/${r.req_id}`} className="text-[#1E5AA8] hover:underline">{r.req_no}</Link></td>
                    <td className={TD}>{r.branch_name}</td>
                    <td className={TD}>{r.desig_name}</td>
                    <td className={TD}>{r.status}{r.pending_with ? ` · ${r.pending_with}` : ""}</td>
                    <td className={`${TD} tabular-nums ${r.days_open > 45 ? "text-[#BB162B] font-semibold" : ""}`}>{r.days_open}</td>
                  </tr>
                ))}
                {d && d.branch_open.length === 0 && <tr><td colSpan={5} className="px-2 py-6 text-center text-xs text-[#6B7280]">No open positions</td></tr>}
              </tbody>
            </table>
          </div>
          {d?.branch_open?.length > 15 && (
            <button data-testid="vp-show-more" onClick={() => setShowAll((s) => !s)}
              className="mt-2 text-xs text-[#1E5AA8] hover:underline">
              {showAll ? "Show less" : `Show all ${d.branch_open.length}`}
            </button>
          )}
        </div>

        <div className="bg-white border border-gray-200 rounded-sm p-4 sm:p-5" data-testid="vp-zh-tat">
          <h2 className="font-serif text-base text-[#0E2240] mb-3">Zonal Head approval turnaround</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr><th className={TH}>Approver</th><th className={TH}>Decisions</th><th className={TH}>Avg TAT (hrs)</th><th className={TH}>&gt;24h</th><th className={TH}>Auto-Escalated</th></tr></thead>
              <tbody>
                {(d?.zh_tat || []).map((r) => (
                  <tr key={r.approver}>
                    <td className={TD}>{r.approver}</td>
                    <td className={`${TD} tabular-nums`}>{r.count}</td>
                    <td className={`${TD} tabular-nums ${r.avg_tat_hours > 24 ? "text-[#BB162B] font-semibold" : ""}`}>{r.avg_tat_hours}</td>
                    <td className={`${TD} tabular-nums`}>{r.breaches}</td>
                    <td className={`${TD} tabular-nums`}>{r.auto_escalated}</td>
                  </tr>
                ))}
                {d && d.zh_tat.length === 0 && <tr><td colSpan={5} className="px-2 py-6 text-center text-xs text-[#6B7280]">No Zonal Head decisions recorded yet</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
